import {useAtomValue} from 'jotai';
import {atomWithLazy} from 'jotai/utils';
import React, {useCallback, useEffect, useRef} from 'react';
import {NavigateOptions, To, useLocation, useNavigate} from 'react-router';

const distanceKey = Symbol();
const pathnameKey = Symbol();
export const getDistance = (entryPoint: NavigationEntryPoint) => entryPoint[distanceKey];
export const getPathname = (entryPoint: NavigationEntryPoint) => entryPoint[pathnameKey];

export interface NavigationEntryPoint {
    // FIXME: (non critical) This will not be updated on POPSTATE events,
    // only on explicit navigation with navigateFromEntry function
    // Need to somehow correctly detect popstate and avoid duplication of
    // counter decrements. This is (non critical) because realistically this issues
    // is only relevant on Android devices which aren't a lot in our user base.
    [distanceKey]: number;
    [pathnameKey]: string;
}

const entrypointRegistry$ = atomWithLazy(() => new Map<string, NavigationEntryPoint>());

export function useNavigationEntryPoint() {
    const {pathname} = useLocation();
    const entrypointRegistry = useAtomValue(entrypointRegistry$);
    const entryPointRef = useRef<NavigationEntryPoint>({
        [distanceKey]: 0,
        [pathnameKey]: pathname,
    });

    useEffect(() => {
        const existingEntryPoint = entrypointRegistry.get(pathname);
        if (existingEntryPoint) {
            entryPointRef.current = existingEntryPoint;
        } else {
            entrypointRegistry.set(pathname, entryPointRef.current);
        }
    }, []);

    return entryPointRef.current;
}

export function useNavigateFromEntry(entryPoint: NavigationEntryPoint) {
    const navigate = useNavigate();
    const navigateFromEntry: typeof navigate = (..._args) => {
        const args = _args as [delta: number] | [to: To] | [to: To, options: NavigateOptions];

        if (args.length === 2) {
            if (args[1]?.replace) {
                // navigate('/address', {replace: true})
                return navigate(...args);
            } else {
                // navigate('/address', {...options, replace?: false})
                const whatever = navigate(...args);
                entryPoint[distanceKey] += 1;
                return whatever;
            }
        } else {
            const arg = args[0];
            if (typeof arg === 'number') {
                // navigate(-2)
                const whatever = navigate(arg);
                entryPoint[distanceKey] += arg;
                return whatever;
            } else {
                // navigate('/address')
                const whatever = navigate(arg);
                entryPoint[distanceKey] += 1;
                return whatever;
            }
        }
    };
    return useCallback(navigateFromEntry, [navigate]);
}

/**
 * FIXME: HACK: the distance is counted manually and gets out of sync with the actual history
 * when an entry is replaced outside of navigateFromEntry.
 *
 * Decrements the distance by the given number of steps. Call it right before entries, pushed with
 * navigateFromEntry, are replaced by a page outside the flow: when the user comes back,
 * those entries no longer exist in history, and without the correction NavigateToEntry
 * would step back further than the entry point and leave the flow.
 */
export function forgetEntrySteps(entryPoint: NavigationEntryPoint, steps: number) {
    entryPoint[distanceKey] = Math.max(0, entryPoint[distanceKey] - steps);
}

export function NavigateToEntry({entry, children}: React.PropsWithChildren<{entry: NavigationEntryPoint}>) {
    const navigate = useNavigate();

    useEffect(() => {
        const distance = getDistance(entry);
        if (distance === 0) {
            navigate(getPathname(entry), {replace: true});
        } else {
            navigate(-1 * distance);
        }
    }, []);

    return children;
}
